import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

/** A JSON-encoded field value ("\"High\"", "true", "null") back into a real value to assign. */
export const parseFieldValue = (raw: string | null): unknown => {
  if (raw === null) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
};

/** Same JSON-encoded value, but for display — quoted strings show unquoted. */
export const displayFieldValue = (raw: string | null): string => {
  if (raw === null) return '(empty)';
  const parsed = parseFieldValue(raw);
  return typeof parsed === 'string' ? parsed : JSON.stringify(parsed);
};

/** "PriorityInfo.Priority" -> "Priority" — last segment, spaced before caps. Intentionally not a
 * full label registry (see spec 010 task notes). */
export const humanizeFieldPath = (path: string): string => {
  const last = path.split('.').pop() ?? path;
  return last.replace(/([a-z0-9])([A-Z])/g, '$1 $2');
};

/** Sets a value at a dotted, PascalCase backend path (e.g. "PriorityInfo.Priority", mirroring
 * TicketMergeService's Path scheme) onto a camelCase TicketView clone. */
export const setByBackendPath = (target: Record<string, any>, path: string, value: unknown): void => {
  const segments = path.split('.').map(seg => seg.charAt(0).toLowerCase() + seg.slice(1));
  let node = target;
  for (let i = 0; i < segments.length - 1; i++) {
    const key = segments[i];
    if (typeof node[key] !== 'object' || node[key] === null) node[key] = {};
    node = node[key];
  }
  node[segments[segments.length - 1]] = value;
};

export const isFetchBaseQueryError = (error: unknown): error is FetchBaseQueryError =>
  typeof error === 'object' && error !== null && 'status' in error;
